import { TABLE_ROW_HEIGHT_PX } from '@/config/constants';
import { ChartProvider } from '@/features/charts/context/ChartContext';
import { CsvDownloadProvider } from '@/features/charts/context/CsvTableContext';
import { LightModeProvider } from '@/shared/lib/LightModeContext';

import type { BaseTabType } from '../../types';
import { MarketWidgetProvider } from '../contexts/MarketWidgetContext';
import { MarketWidget } from '../MarketWidget';

type WidgetContentProps = {
  tab: BaseTabType;
  /**
   * Pixel height for the rendered content. Widget `layout.height` is measured
   * in table-row units (see TABLE_ROW_HEIGHT_PX), not pixels — this prop
   * converts so callers can size a widget in real pixels.
   */
  heightPx?: number;
};

/**
 * Renders a single widget's real content — chart, table, feed, etc. — via the
 * same `MarketWidget` dispatch the dashboard uses, wrapped in the providers
 * that dispatch depends on (chart context, CSV download, light mode).
 *
 * This is the one place that knows how to render "just this widget" outside
 * a full `TabsContainer` (title bar, tabs, export/share actions) — reuse it
 * anywhere a widget needs to render standalone, instead of composing
 * `MarketWidget` + providers ad hoc at each call site.
 */
export const WidgetContent = ({ tab, heightPx }: WidgetContentProps) => {
  const layout = heightPx
    ? { ...tab.layout, height: Math.max(1, Math.round(heightPx / TABLE_ROW_HEIGHT_PX)) }
    : tab.layout;

  return (
    <MarketWidgetProvider>
      <ChartProvider>
        <CsvDownloadProvider>
          <LightModeProvider>
            <MarketWidget tab={{ ...tab, layout }} />
          </LightModeProvider>
        </CsvDownloadProvider>
      </ChartProvider>
    </MarketWidgetProvider>
  );
};
